import React, {useEffect, useMemo, useState} from "react";
import Button from "../../../../../../../components/base/button/button";
import {Col, Row} from "reactstrap";
import PreviewBannerDialog from "../../../../../../../components/dialogs/preview-banner";
import Api from "../../../../../../../../core/services/api/api";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import {bannerWidthToHeightRatio} from "../../../../../../../../core/constants/enums";
import Banner from "../../../../../../../components/app-specific/banner";
import {Add} from "@mui/icons-material";
import BannerSelectionDialog from "../../../../../../../components/dialogs/banner-selection";


const TagConnectedBanners = ({connectedBanners, setTagInfo, industry, tagId}) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedBanner, setSelectedBanner] = useState(null);
    const [data, setData] = useState(connectedBanners ?? []);
    const [addingNewBannersCount, setAddingNewBannersCount] = useState(0);
    const [openBannerSelectionDialog, setOpenBannerSelectionDialog] = useState(false);
    const isMounted = useIsMounted();

    const existingBanners = useMemo(() => data?.map(e => ({id: e.bannerId})) ?? [], [data]);

    /**
     * With each change in the connectedBanners props:
     * - syncs the data list of the state with the connected banners.
     */
    useEffect(() => {
        setData(connectedBanners ?? []);
    }, [connectedBanners])

    /**
     * Handles connecting the banner to the tag
     * - Calls the api for connecting a banner to the tag
     *
     * @param banner
     * @return {Promise<void>}
     */
    const reconnectBannerToTag = async (banner) => {
        setData(prevState => prevState.map(e => e.connectionId === banner.connectionId ? {...e, loading: true} : e))
        const response = await Api.addTagToBanner(banner.bannerId, tagId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.connectionId === banner.connectionId ? {
                ...e,
                connectionId: response?.data?.id ?? e.connectionId,
                isDeleted: false,
                loading: false
            } : e));
            setTagInfo(prevState => ({...prevState, connectedBannerCount: prevState.connectedBannerCount + 1}))
            setIsDialogOpen(false);
            return;
        }
        setData(prevState => prevState.map(e => e.connectionId === banner.connectionId ? {...e, loading: false} : e))
    }

    /**
     * Connects a newly selected banner to the current tag.
     *
     * @param {any} banner
     * @return {Promise<any>}
     */
    const connectBannerToTag = async (banner) => {
        const response = await Api.addTagToBanner(banner.id, tagId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setIsDialogOpen(false);
            setTagInfo(prevState => ({...prevState, connectedBannerCount: prevState.connectedBannerCount + 1}))
            return {
                connectionId: response.data?.id,
                bannerId: response.data?.bannerId,
                fileName: banner?.imageFileInfo?.url,
                isDeleted: false,
                loading: false,
            }
        }
    }

    /**
     * Connects the newly selected banners to the current tag.
     * @param {any[]} banners
     * @return {Promise<void>}
     */
    const onNewBannersSelected = async (banners) => {
        setAddingNewBannersCount(banners.length);
        const all = [];
        for (const banner of banners) {
            all.push(connectBannerToTag(banner));
        }
        const addedBanners = (await Promise.all(all))?.filter(e => !!e) ?? [];
        setAddingNewBannersCount(0);
        setData(prevState => [
            ...prevState,
            ...addedBanners
        ])
    }

    /**
     * Handles deletion of a banner:
     * - Calls the api for removing the connection of a tag from banner
     *
     * @param banner
     */
    const removeBannerConnectionFromTag = async (banner) => {
        setData(prevState => prevState.map(e => e.connectionId === banner.connectionId ? {...e, loading: true} : e))
        const response = await Api.deleteTagBannerConnection(banner.connectionId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.connectionId === banner.connectionId ? {
                ...e,
                isDeleted: true,
                loading: false
            } : e))
            setTagInfo(prevState => ({...prevState, connectedBannerCount: prevState.connectedBannerCount - 1}))
            setIsDialogOpen(false);
            return;
        }
        setData(prevState => prevState.map(e => e.connectionId === banner.connectionId ? {...e, loading: false} : e))
    }

    /**
     * Handles showing a dialog for previewing the banner
     * @param banner
     */
    const onBannerClicked = (banner) => {
        setSelectedBanner(banner);
        setIsDialogOpen(true);
    }

    /**
     * Handles click on connect banner button:
     * Opens dialog for adding banners to connections
     */
    const onAddBannerClicked = () => {
        setOpenBannerSelectionDialog(true);
    }

    return (
        <>
            <div className={'tag-connected-banners-view'}>
                <div className={'d-flex flex-row justify-content-between align-items-center w-100 mt-4 mb-4 px-3'}>
                    <div/>
                    <Button
                        className='justify-content-center align-items-center d-flex'
                        variant={'outlined'}
                        color={'primary'}
                        onClick={onAddBannerClicked}
                    >
                        <Add className='icon normal p-1 mr-2'/>
                        <p className={'button-label'}>Connect Banners</p>
                    </Button>
                </div>
                <Row className={'w-100 d-flex flex-wrap align-items-center'}>
                    {
                        (data?.length ?? 0) === 0 &&
                        <div className={'no-item-was-found'}>
                            <p>
                                Currently there is no item in this list.
                            </p>
                        </div>
                    }
                    {
                        data?.map(banner =>
                            <Col xs={12} sm={12} md={6} xl={4}
                                 key={banner.connectionId}
                                 className={'d-flex flex-column justify-content-center align-items-center p-4'}
                            >
                                <div
                                    className={'cursor-pointer-hover w-100'}
                                    onClick={() => onBannerClicked(banner)}
                                >
                                    <Banner
                                        url={banner?.fileName}
                                        widthToHeightAspectRatio={bannerWidthToHeightRatio}
                                    />
                                </div>
                                {
                                    banner.isDeleted
                                        ? <Button
                                            className='mt-2 w-80'
                                            variant={'contained'}
                                            color={'primary'}
                                            disabled={banner.loading}
                                            onClick={() => reconnectBannerToTag(banner)}
                                        >
                                            {
                                                banner.loading
                                                    ? "Reconnecting..."
                                                    : "Reconnect Again"
                                            }
                                        </Button>
                                        : <Button
                                            className='mt-2 w-80'
                                            variant={'outlined'}
                                            color={'primary'}
                                            disabled={banner.loading}
                                            onClick={() => removeBannerConnectionFromTag(banner)}
                                        >
                                            {
                                                banner.loading
                                                    ? "Removing..."
                                                    : "Remove Connection"
                                            }

                                        </Button>
                                }

                            </Col>
                        )
                    }
                    {
                        Array(addingNewBannersCount).fill(null).map((_, e) => (
                            <Col xs={12} sm={12} md={6} lg={4} xl={3}
                                 key={`adding-${e}`}
                                 className={'loading extraLarge p-4'}
                            >
                                <div/>
                            </Col>
                        ))
                    }
                </Row>
            </div>
            <PreviewBannerDialog
                open={isDialogOpen}
                close={() => setIsDialogOpen(false)}
                bannerIcon={selectedBanner?.fileName}
                reconnectBannerToTagCallBack={() => reconnectBannerToTag(selectedBanner)}
                removeBannerTagConnectionCallBack={() => removeBannerConnectionFromTag(selectedBanner)}
                isDeletedAlready={selectedBanner?.isDeleted}
            />
            <BannerSelectionDialog
                existingBanners={existingBanners}
                open={openBannerSelectionDialog}
                close={() => setOpenBannerSelectionDialog(false)}
                onSuccess={onNewBannersSelected}
                multiSelect
                excludeExistingBanners
                industry={industry}
            />
        </>
    );
}

export default TagConnectedBanners;
